import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Gift, ArrowRight, CalendarClock, MapPin, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FacebookIcon } from '../FacebookIcon';
import { InstagramIcon } from '../InstagramIcon';
import { ContestCountdown } from './ContestCountdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CONTENT = {
  title: 'Win a Cash Prize!',
  prize1: '$300',
  prize2: '$200',
  prize3: '$100',
  intro: "We're inviting our entire childcare community — daycare providers, parents, childcare workers, nannies, babysitters, teachers & camps — to join our giveaway. Three winners will be drawn!",
  steps: [
    { title: 'Create your free account', desc: 'Sign up on ChildCareConnections.ca — it only takes a minute.' },
    { title: 'Follow & share', desc: 'Follow our Facebook / Instagram page and share the web-app with your network.' },
    { title: 'Leave your feedback', desc: 'Post your honest review — tell us what you love, which feature is most useful, and how likely you are to recommend us.' },
  ],
  deadline_label: 'September 30, 2026',
  deadline_date: '2026-09-30',
  eligibility: 'Contest open to Canadian residents only.',
  facebook_url: 'https://www.facebook.com/profile.php?id=61575771127335',
  instagram_url: 'https://www.instagram.com/childcareconnections.ca',
};

const resolveUrl = (u) => (!u ? null : (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

// Contest / giveaway rules dialog. Content is admin-editable (GET /api/app-reviews/contest).
// - Landing (dashboard=false): CTA routes logged-out visitors to sign-up, logged-in users to the dashboard.
// - Dashboard (dashboard=true): step 1 is shown as done; CTA "Continue to review" opens the review modal.
export function ContestRulesDialog({ open, onOpenChange, dashboard = false, onContinueToReview, previewContent = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [imgUrl, setImgUrl] = useState(null);
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/app-reviews/campaign-image`)
      .then(r => setImgUrl(r.data?.active ? resolveUrl(r.data.url) : null))
      .catch(() => setImgUrl(null));
    if (previewContent) {
      setContent({ ...DEFAULT_CONTENT, ...previewContent, steps: (previewContent.steps?.length ? previewContent.steps : DEFAULT_CONTENT.steps) });
    } else {
      axios.get(`${API}/app-reviews/contest`)
        .then(r => setContent({ ...DEFAULT_CONTENT, ...(r.data || {}), steps: (r.data?.steps?.length ? r.data.steps : DEFAULT_CONTENT.steps) }))
        .catch(() => setContent(DEFAULT_CONTENT));
    }
  }, [open, previewContent]);

  const c = content || DEFAULT_CONTENT;

  const handleCta = () => {
    onOpenChange(false);
    if (previewContent) return;
    if (dashboard) { onContinueToReview && onContinueToReview(); return; }
    // Not logged in → send to sign-up (default) or login (via the second CTA). Both preserve
    // ?contest=1 so the dashboard opens with this dialog again after auth completes, meaning
    // the user's contest-entry flow never drops mid-way.
    navigate(user ? '/dashboard?contest=1' : '/auth?mode=register&contest=1');
  };
  const handleLoginCta = () => {
    onOpenChange(false);
    if (previewContent) return;
    navigate('/auth?mode=login&contest=1');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-white/40 rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contest-rules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Fredoka' }}>
            <Gift className="w-5 h-5 text-fuchsia-500" /> {c.title}
          </DialogTitle>
          <DialogDescription className="sr-only">Contest entry details, prizes and how to participate.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Flyer */}
          {imgUrl && (
            <div className="rounded-2xl overflow-hidden border border-white/50 shadow-sm bg-white/40">
              <img
                src={imgUrl}
                alt="ChildCareConnections cash prize giveaway"
                className="w-full h-auto object-contain"
                data-testid="contest-flyer"
                loading="lazy"
              />
            </div>
          )}

          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Fredoka' }}>
              Win a <span className="text-rose-500">{c.prize1}</span>, <span className="text-sky-500">{c.prize2}</span>, or <span className="text-emerald-500">{c.prize3}</span> cash prize!
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">{c.intro}</p>
          </div>

          {/* Steps */}
          <div className="space-y-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">How to enter</p>
            {(c.steps || []).map((s, i) => {
              const done = dashboard && i === 0;
              return (
                <div key={i} className={`flex items-start gap-3 rounded-xl border p-3 ${done ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white/50 border-white/50'}`} data-testid={`contest-step-${i + 1}`}>
                  <span className={`w-7 h-7 shrink-0 rounded-full text-white text-sm font-semibold flex items-center justify-center ${done ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ fontFamily: 'Fredoka' }}>
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      <span className={done ? 'text-slate-400 line-through' : 'text-slate-800'}>{s.title}</span>
                      {done && <span className="text-[10px] font-semibold text-emerald-600 px-1.5 py-0.5 rounded-full bg-emerald-100">Done</span>}
                    </p>
                    <p className={`text-xs leading-snug ${done ? 'text-slate-400 line-through' : 'text-slate-500'}`}>{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Deadline + eligibility */}
          <div className="grid gap-2 text-sm">
            <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800">
              <CalendarClock className="w-4 h-4 shrink-0" />
              <span>Deadline: <strong>{c.deadline_label}</strong> — <ContestCountdown deadline={c.deadline_date} /></span>
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-slate-600">
              <MapPin className="w-4 h-4 shrink-0" />
              <span>{c.eligibility}</span>
            </div>
          </div>

          {/* Social links */}
          <div className="flex items-center justify-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Follow us:</span>
            <a href={c.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="contest-facebook-link"
              aria-label="Follow ChildCareConnections on Facebook"
              className="flex items-center justify-center w-9 h-9 rounded-full text-[#1877F2] bg-white/70 hover:bg-[#1877F2]/10 transition-colors ring-1 ring-slate-200">
              <FacebookIcon className="w-5 h-5" />
            </a>
            <a href={c.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="contest-instagram-link"
              aria-label="Follow ChildCareConnections on Instagram"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-white/70 hover:bg-[#d6249f]/10 transition-colors ring-1 ring-slate-200">
              <InstagramIcon className="w-5 h-5" />
            </a>
          </div>

          <Button data-testid="contest-cta" onClick={handleCta} className="w-full rounded-xl h-11 text-white hover:opacity-95 transition-opacity"
            style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 40%, #d946ef 100%)' }}>
            {dashboard ? 'Continue to review' : (user ? 'Go to my dashboard' : 'Create your free account')} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          {!dashboard && !user && !previewContent && (
            <button data-testid="contest-login-cta" onClick={handleLoginCta}
              className="w-full text-center text-sm text-slate-600 hover:text-violet-600 font-medium py-2 -mt-1">
              I already have an account — log in
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ContestRulesDialog;
